import { useMemo } from 'react';
import {
  Modal,
  ModalSizes,
  PDFViewer,
  Alert,
  AlertVariants,
  AlertAppearances,
  AlertOrientations,
  Button,
  ButtonVariants,
  ButtonSizes,
} from '@carboledger/ui';
import { Pencil, Download } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import { rowsShareDeclarationFields } from '../constants/allocationBulkGenerate';
import styles from './GenerateDeclarationModal.module.scss';

/**
 * Props:
 *   open              whether the modal is shown
 *   onClose           ✕ / Close
 *   allocations       the rows this declaration covers — one, or a bulk selection
 *   file              the rendered declaration — a Blob <PDFViewer> takes directly
 *   isSaving          disables the primary CTA while the mutation is in flight
 *   canEdit           enables Edit — open until the one edit after generating is used
 *   onEdit            opens the declaration form (the page warns first once it is generated)
 *   onComplete        opens the form to fill what is missing before generating
 *   onDownload        downloads the draft
 *   onConfirm         confirms and downloads — only offered when nothing is missing
 */
export const GenerateDeclarationModal = ({
  open,
  onClose,
  allocations = EMPTY_LIST,
  file,
  isSaving,
  canEdit = false,
  onEdit,
  onComplete,
  onDownload,
  onConfirm,
}) => {
  const t = useTranslations();
  const editIcon = useMemo(() => <Pencil />, []);
  const downloadIcon = useMemo(() => <Download />, []);

  // A bulk selection generates one declaration, so every row must agree and be ready.
  const fieldsMatch = rowsShareDeclarationFields(allocations);
  const isComplete = fieldsMatch && allocations.every((row) => row.requiredFieldsComplete);

  const title = useMemo(
    () =>
      t('ALLOCATIONS_DECLARATION_PREVIEW_TITLE', {
        transactionIds: allocations.map((row) => row.transactionId).join(', '),
      }),
    [t, allocations],
  );

  const buttons = useMemo(
    () => [
      { label: t('COMMON_CLOSE'), variant: ButtonVariants.neutral, onClick: onClose },
      isComplete
        ? {
            label: t('ALLOCATIONS_ACTION_CONFIRM_DECLARATION'),
            variant: ButtonVariants.primary,
            onClick: onConfirm,
            disabled: isSaving,
          }
        : {
            // Rows that disagree can't be edited into one declaration either.
            label: fieldsMatch
              ? t('ALLOCATIONS_ACTION_EDIT_DECLARATION')
              : t('ALLOCATIONS_ACTION_CONFIRM_DECLARATION'),
            variant: ButtonVariants.primary,
            onClick: fieldsMatch ? onComplete : onConfirm,
            disabled: !fieldsMatch,
          },
    ],
    [t, onClose, fieldsMatch, isComplete, onConfirm, onComplete, isSaving],
  );

  return (
    <Modal open={open} onClose={onClose} size={ModalSizes.large} title={title} buttons={buttons}>
      <div className={styles.body}>
        {!fieldsMatch && (
          <Alert
            variant={AlertVariants.error}
            appearance={AlertAppearances.accent}
            orientation={AlertOrientations.horizontal}
          >
            {t('ALLOCATIONS_BULK_FIELDS_MISMATCH')}
          </Alert>
        )}

        <div className={styles.toolbar}>
          <Button
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            leftIcon={editIcon}
            onClick={onEdit}
            disabled={!canEdit}
          >
            {t('ALLOCATIONS_ACTION_EDIT_DECLARATION')}
          </Button>
          {/* Always offered: the stored file when there is one, else the preview shown here. */}
          <Button
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            leftIcon={downloadIcon}
            onClick={onDownload}
          >
            {t('ALLOCATIONS_ACTION_DOWNLOAD_DECLARATION')}
          </Button>
        </div>

        <PDFViewer
          className={styles.viewer}
          file={file}
          showToolbar={false}
          showSidebar={false}
          fitToWidth
          noFileLabel={t('PDF_VIEWER_NO_FILE')}
        />
      </div>
    </Modal>
  );
};
